import { useAvailableShippingRules, usePortalTranslate } from '@/hooks'
import {
  ShippingRule,
  useSaveShippingConfig,
  useShippingConfig
} from '@/application/manage-shipping'
import { useChatContext, useRegisterChatContext } from '@/ui/components/ai-chat'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import { normalizeChatRules } from './normalize-chat-rules'
import {
  Draft,
  RuleTable,
  countChanges,
  countIncomplete,
  draftFromRules,
  fromDraft,
  toDraft
} from '@/ui/components/shipping-costs'
import { useEffect, useState } from 'react'
import { Base, ReverLoading } from '@itsrever/design-system'
import { useCountries } from '@/application/queries'
import { LoadError, NavigationTabs, PageHeader, SaveBar } from '@/ui/shared'
import { useLogisticsTabs } from './configuration-tabs'
import { useTranslation } from 'react-i18next'
import {
  isDashboardBlocked,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { ReverAiMark } from '@/ui/components/ai-widget/ai-widget'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'

export default function Shipping() {
  const { t } = useTranslation()
  const { isFlagEnabled } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'configuration'
  })
  const { countries, loadingCountries, reloadCountries } = useCountries()
  const { originalShippingRules, loadingShippingConfig, reloadShippingConfig } =
    useShippingConfig(countries ?? [])
  const {
    availableConfiguration,
    loadingAvailableRules,
    availableRulesFailed,
    reasonKeys,
    reloadAvailableRules
  } = useAvailableShippingRules(countries ?? [])
  const { loadingNewShippingConfig, saveShippingConfig } =
    useSaveShippingConfig(availableConfiguration, countries ?? [], reasonKeys)

  const { trans } = usePortalTranslate({})

  const role = useAppSelector((s) => s.userApi.getMe.response.user?.role)
  const canEdit = role === Roles.ReadWrite

  const { isChatAvailable, openChat } = useChatContext()
  const stored = originalShippingRules ?? []
  const [draft, setDraft] = useState<Draft>(() => toDraft(stored))
  // New stored rules (the first load, a successful save) replace the edits.
  useEffect(() => {
    if (originalShippingRules) setDraft(toDraft(originalShippingRules))
  }, [originalShippingRules])

  useRegisterChatContext<{ rules: ShippingRule[] }, { rules: ShippingRule[] }>({
    tab: 'shipping-costs',
    getPayload: () => ({ rules: fromDraft(draft) }),
    applyResult: (result) => {
      if (!isShippingResult(result)) return false
      const normalized = normalizeChatRules(
        result.rules,
        availableConfiguration,
        t,
        { keys: reasonKeys, trans }
      )
      setDraft(draftFromRules(normalized, stored))
      return true
    }
  })

  const tabs = useLogisticsTabs()
  const changes = countChanges(draft, stored)
  const incomplete = countIncomplete(draft, stored)

  if (loadingAvailableRules || loadingCountries || loadingShippingConfig)
    return <ReverLoading />
  // An editor started without the stored rules would save over every one of them.
  if (!originalShippingRules || availableRulesFailed)
    return (
      <>
        <NavigationTabs tabs={tabs} selectedIndex={0} />
        <PageHeader
          title={t('shipping_costs.title')}
          description={t('shipping_costs.description')}
        />
        <div className="px-7 py-6">
          <LoadError
            message={t('shipping_costs.load_error')}
            onRetry={() => {
              reloadCountries()
              reloadShippingConfig()
              reloadAvailableRules()
            }}
          />
        </div>
      </>
    )
  return (
    <>
      <NavigationTabs tabs={tabs} selectedIndex={0} />
      {isFlagEnabled && <BlockedDashboardOverlay />}
      <PageHeader
        title={t('shipping_costs.title')}
        description={t('shipping_costs.description')}
      />
      <div className="flex flex-col gap-6 px-7 py-6 pb-24">
        <RuleTable
          draft={draft}
          stored={stored}
          availableConfiguration={availableConfiguration}
          editable={canEdit}
          onChange={setDraft}
          actions={
            canEdit && isChatAvailable ? (
              <Base.Button
                type="button"
                variant="primary"
                size="sm"
                onClick={openChat}
              >
                <ReverAiMark width={14} height={16} />
                {t('edit_shipping.edit_with_ai')}
                <span className="ml-0.5 rounded bg-white/20 px-1.5 text-xs font-medium uppercase tracking-wide">
                  {t('shipping_rules.new_badge')}
                </span>
              </Base.Button>
            ) : null
          }
        />
      </div>
      <SaveBar
        editable={canEdit}
        changes={changes}
        problem={
          incomplete
            ? t('shipping_rules.incomplete', { count: incomplete })
            : undefined
        }
        saving={loadingNewShippingConfig}
        onDiscard={() => setDraft(toDraft(stored))}
        onSave={() => saveShippingConfig(fromDraft(draft))}
      />
    </>
  )
}

// The chat result crosses an untyped boundary; validate the display shape
// before touching the draft so a malformed reply can never corrupt it.
function isShippingResult(x: unknown): x is { rules: ShippingRule[] } {
  if (typeof x !== 'object' || x === null) return false
  const { rules } = x as { rules?: unknown }
  return Array.isArray(rules) && rules.every(isShippingRule)
}

function isShippingRule(rule: unknown): rule is ShippingRule {
  if (typeof rule !== 'object' || rule === null) return false
  const { params, returnOriginalShippingCosts, conditions } = rule as Record<
    string,
    unknown
  >
  if (typeof params !== 'object' || params === null) return false
  const { cost } = params as Record<string, unknown>
  return (
    typeof cost === 'string' &&
    cost.length > 0 &&
    !isNaN(Number(cost)) &&
    typeof returnOriginalShippingCosts === 'boolean' &&
    Array.isArray(conditions) &&
    conditions.every(isShippingCondition)
  )
}

function isShippingCondition(condition: unknown): boolean {
  if (typeof condition !== 'object' || condition === null) return false
  const {
    function: fn,
    comparison,
    values
  } = condition as Record<string, unknown>
  return (
    typeof fn === 'string' &&
    fn !== '' &&
    typeof comparison === 'string' &&
    comparison !== '' &&
    Array.isArray(values) &&
    values.length > 0 &&
    values.every((value) => typeof value === 'string')
  )
}
